import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FieldTypeSelectorComponent } from '../../pages/form-builder/fields/shared/field-type-selector/field-type-selector.component';
import { LoggerService } from '../services/logger.service';

/**
 * Formly wrapper for FieldTypeSelectorComponent
 *
 * Allows the field type selector to be used as a Formly field type in config editor forms.
 * This enables the field type selector to be part of the Formly form definition rather than
 * a hard-coded exception outside the form.
 *
 * Usage in FormlyFieldConfig:
 * ```typescript
 * {
 *   key: 'type',
 *   type: 'field-type-selector',
 *   props: {
 *     isOpen: false, // controls accordion expansion (true for new fields)
 *   },
 * }
 * ```
 *
 * The wrapper:
 * - Passes the model object as the `field` input to FieldTypeSelectorComponent
 * - Listens to `onFieldTypeSelected` and writes the new type back to Formly's model
 * - Formly's `(modelChange)` on `<formly-form>` will detect this change and parent can handle it
 */
@Component({
  selector: 'app-field-type-selector-wrapper',
  standalone: true,
  imports: [FieldTypeSelectorComponent],
  template: `
    <app-field-type-selector
      [field]="model"
      [isOpen]="props['isOpen'] || false"
      (onFieldTypeSelected)="onTypeSelected($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      flex-basis: 100%;
    }
  `,
})
export class FieldTypeSelectorWrapperComponent extends FieldType<FieldTypeConfig> {
  private log = inject(LoggerService).getLogger('field-type-selector');

  onTypeSelected(newType: string): void {
    this.log.debug('[FieldTypeSelectorWrapper.onTypeSelected] 🔵 Entry - newType:', newType);
    this.log.debug(
      '[FieldTypeSelectorWrapper.onTypeSelected] 📊 Current model.type:',
      this.model?.type,
    );
    this.log.debug(
      '[FieldTypeSelectorWrapper.onTypeSelected] 📊 Model object:',
      JSON.stringify(this.model, null, 2),
    );

    // Update the model's type property directly
    // This triggers Formly's (modelChange) event which parent component detects
    if (this.model && this.model.type !== newType) {
      this.log.debug(
        '[FieldTypeSelectorWrapper.onTypeSelected] 🔄 Updating model.type from',
        this.model.type,
        'to',
        newType,
      );

      this.model.type = newType;
      this.log.debug(
        '[FieldTypeSelectorWrapper.onTypeSelected] ✅ model.type updated to:',
        this.model.type,
      );
      this.log.debug(
        '[FieldTypeSelectorWrapper.onTypeSelected] 📊 Updated model object:',
        JSON.stringify(this.model, null, 2),
      );

      // Also update form control to keep form state in sync
      this.formControl.setValue(newType);
      this.log.debug('[FieldTypeSelectorWrapper.onTypeSelected] ✅ formControl.setValue() called');

      // Manually trigger options.formState update to propagate change
      this.options.formState = { ...this.options.formState };
      this.log.debug(
        '[FieldTypeSelectorWrapper.onTypeSelected] ✅ options.formState updated to propagate change',
      );
    } else {
      this.log.debug(
        '[FieldTypeSelectorWrapper.onTypeSelected] ⚠️ No update needed - model null or type unchanged',
      );
    }
  }
}
